/** Platform-independent offline command ownership, replay, and ledger rules. */

/** Every write that may be captured while offline. Nothing else is eligible. */
export type OfflineMutationKind =
  | 'task.create'
  | 'task.status'
  | 'time-entry.create'
  | 'thread-comment.create'

/**
 * Mutation keys registered through `setMutationDefaults`. The leading
 * `'offline'` segment is load-bearing: it is what `isOfflineMutationKey`
 * matches on, and therefore what decides whether a paused mutation is written
 * to disk at all.
 */
export const OFFLINE_MUTATION_KEYS: Record<
  OfflineMutationKind,
  readonly string[]
> = {
  'task.create': ['offline', 'task', 'create'],
  'task.status': ['offline', 'task', 'status'],
  'time-entry.create': ['offline', 'time-entry', 'create'],
  'thread-comment.create': ['offline', 'thread-comment', 'create'],
}

export type ReplaySafety =
  | 'server-deduped'
  | 'naturally-idempotent'
  | 'at-most-once'

export const REPLAY_SAFETY: Record<OfflineMutationKind, ReplaySafety> = {
  // POST /tasks has no idempotency key server-side. A duplicate is a duplicate
  // task — recoverable, but noise the user has to clean up.
  'task.create': 'at-most-once',
  // PUT /tasks/:id/status is a set-to-value write, so replaying re-asserts the
  // same status. Recurring spawn-on-completion is itself guarded by a
  // compare-and-swap claim server-side, so a re-assert cannot double-spawn.
  'task.status': 'naturally-idempotent',
  // POST /time-entries has no idempotency key. A duplicate is double-logged
  // hours flowing into timesheets and billing. Never replay blind.
  'time-entry.create': 'at-most-once',
  // thread_comment carries UNIQUE (threadId, authorId, clientMessageId) and the
  // repository turns the resulting P2002 into "return the existing row". This
  // is the pattern the rest of the queue is modelled on.
  'thread-comment.create': 'server-deduped',
}

/**
 * Documentation-only, and deliberately so: it exists to make the next person
 * adding a mutation key decide which side of the line it falls on.
 *
 * A queued financial write that fires on reconnect against context that moved
 * hours ago is strictly worse than an error message the user can act on.
 */
export const OFFLINE_EXCLUSIONS = [
  'Financial: invoice create/send/void, payment record/refund, quote send/accept, credit purchases, subscription and billing changes, contract plan changes.',
  'Approvals: expense approval, timesheet approval, submit-for-payroll, payroll runs.',
  'Destructive: deletes, merges, archive/restore — a replayed delete cannot be undone.',
  'Privileged: permission, role, membership and organization-settings changes.',
  'Binary bodies: file and attachment uploads do not survive JSON serialization.',
  'Writes whose target was itself created offline — the queue has no dependency ordering, so commenting on an unsynced task is refused at the composer.',
] as const

/** How long a captured write stays replayable before it is treated as stale. */
export const OFFLINE_QUEUE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000

/** Replay attempts before a write stops retrying and asks for a decision. */
export const OFFLINE_QUEUE_MAX_ATTEMPTS = 5

/** How long settled rows are kept so a late replay can still be deduped. */
export const OFFLINE_LEDGER_RETENTION_MS = 24 * 60 * 60 * 1000

/**
 * The variables of every eligible mutation. `idempotencyKey` is generated once
 * at the call site — NOT inside `mutationFn`, which re-runs on every replay —
 * and is what makes a replay recognizable as the same write.
 */
export interface OfflineMutationEnvelope<TPayload = unknown> {
  version: 2
  ownerId: string
  /** Capture provenance; dispatch uses a fresh session so work survives sign-in. */
  sessionGeneration: number
  idempotencyKey: string
  kind: OfflineMutationKind
  /** Requests carry the org as a header, so it has to travel in the payload. */
  organizationId: string
  enqueuedAt: number
  /** Shown verbatim in the pending-writes list. Keep it human. */
  label: string
  payload: TPayload
}

export type OfflineLedgerStatus =
  | 'queued'
  | 'inflight'
  | 'succeeded'
  | 'failed'
  | 'needs-review'
  | 'expired'

/**
 * The ledger stores the whole envelope, not just metadata. TanStack only
 * persists *paused* mutations, so a write that errored while online would
 * otherwise vanish on reload; keeping the envelope means the pending-writes UI
 * can re-dispatch it from a cold start.
 */
export interface OfflineLedgerEntry {
  envelope: OfflineMutationEnvelope
  status: OfflineLedgerStatus
  attempts: number
  updatedAt: number
  /** Cached server response, replayed verbatim if the same key runs again. */
  result?: unknown
  /** User-facing explanation for `failed` / `needs-review` / `expired`. */
  message?: string
}

export interface OfflineLedger {
  get(idempotencyKey: string): OfflineLedgerEntry | undefined
  list(): OfflineLedgerEntry[]
  upsert(entry: OfflineLedgerEntry): void
  patch(
    idempotencyKey: string,
    changes: Partial<Omit<OfflineLedgerEntry, 'envelope'>>,
  ): OfflineLedgerEntry | undefined
  remove(idempotencyKey: string): void
  subscribe(listener: () => void): () => void
  prune(now?: number): void
  hydrate(entries: OfflineLedgerEntry[]): void
}

/**
 * In-memory ledger with an optional write-through to the host's storage.
 * `persist` is fire-and-forget on purpose: losing a ledger row degrades dedupe
 * to "whatever guard the server itself has", never to data loss.
 */
export function createOfflineLedger(
  persist?: (entries: OfflineLedgerEntry[]) => void,
): OfflineLedger {
  const entries = new Map<string, OfflineLedgerEntry>()
  const listeners = new Set<() => void>()

  const emit = () => {
    const snapshot = [...entries.values()]
    // Only unfinished work reaches disk. Succeeded rows are kept in memory for
    // the life of the session (where same-session replay dedupe needs them) but
    // would otherwise pile up one row per tap for anyone working online.
    persist?.(snapshot.filter(entry => entry.status !== 'succeeded'))
    listeners.forEach(listener => {
      try {
        listener()
      } catch {
        // A broken subscriber must not take the queue down with it.
      }
    })
  }

  const byQueuedAt = (a: OfflineLedgerEntry, b: OfflineLedgerEntry) =>
    a.envelope.enqueuedAt - b.envelope.enqueuedAt

  return {
    get: key => entries.get(key),
    list: () => [...entries.values()].sort(byQueuedAt),
    upsert: entry => {
      entries.set(entry.envelope.idempotencyKey, entry)
      emit()
    },
    patch: (key, changes) => {
      const existing = entries.get(key)
      if (!existing) return undefined
      const next = { ...existing, ...changes, updatedAt: Date.now() }
      entries.set(key, next)
      emit()
      return next
    },
    remove: key => {
      if (entries.delete(key)) emit()
    },
    subscribe: listener => {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
    hydrate: restored => {
      restored.forEach(entry => {
        const current = entries.get(entry.envelope.idempotencyKey)
        if (!current || entry.updatedAt > current.updatedAt)
          entries.set(entry.envelope.idempotencyKey, entry)
      })
      emit()
    },
    prune: (now = Date.now()) => {
      let changed = false
      entries.forEach((entry, key) => {
        if (
          entry.status === 'succeeded' &&
          now - entry.updatedAt > OFFLINE_LEDGER_RETENTION_MS
        ) {
          entries.delete(key)
          changed = true
        }
      })
      if (changed) emit()
    },
  }
}

/**
 * Builds the envelope for one write and records it as `queued` in the same
 * breath.
 *
 * The ledger row has to exist *before* `mutationFn` runs, because when the
 * device is offline it never runs at all — TanStack parks the mutation and the
 * call site returns immediately. Without this the pending-writes UI would read
 * zero for exactly the situation it exists to describe.
 *
 * Call it at the call site, not inside `mutationFn`: `mutationFn` re-executes
 * on every replay, and an idempotency key regenerated per attempt is not an
 * idempotency key.
 */
export function enqueueOfflineWrite<TPayload>(
  ledger: OfflineLedger,
  input: {
    kind: OfflineMutationKind
    ownerId: string
    sessionGeneration?: number
    organizationId: string
    label: string
    payload: TPayload
    idempotencyKey: string
    now?: number
  },
): OfflineMutationEnvelope<TPayload> {
  if (!input.ownerId) throw new OfflineWriteOwnerError()
  const envelope: OfflineMutationEnvelope<TPayload> = {
    version: 2,
    ownerId: input.ownerId,
    sessionGeneration: input.sessionGeneration ?? 0,
    idempotencyKey: input.idempotencyKey,
    kind: input.kind,
    organizationId: input.organizationId,
    enqueuedAt: input.now ?? Date.now(),
    label: input.label,
    payload: input.payload,
  }

  ledger.upsert({
    envelope,
    status: 'queued',
    attempts: 0,
    updatedAt: envelope.enqueuedAt,
  })

  return envelope
}

/**
 * Ordering. `resumePausedMutations` fires every paused mutation at once, which
 * would let a status change land before the create that produced the task.
 * Routing every replay through one promise chain restores FIFO: writes execute
 * in the order the user made them, one at a time, and a failure does not stall
 * the ones behind it.
 */
export function createSerialGate(): <T>(fn: () => Promise<T>) => Promise<T> {
  let tail: Promise<unknown> = Promise.resolve()

  return function run<T>(fn: () => Promise<T>): Promise<T> {
    const result = tail.then(fn, fn)
    tail = result.then(
      () => undefined,
      () => undefined,
    )
    return result
  }
}

/** What to do with a write whose replay just failed. */
export type ReplayDisposition = 'retry' | 'drop' | 'fail'

/**
 * Conflict handling. A replayed write is an ordinary request against whatever
 * the server looks like *now* — the server stays authoritative, always.
 *
 *   no status  The request never got an answer. Still offline, or a flaky
 *              link. Stay queued and try again on the next reconnect.
 *   404 / 410  The target moved on without us: the task was deleted while the
 *              device was offline. There is nothing to apply, so drop it and
 *              say so rather than resurrecting a deleted record.
 *   409 / 412  Someone else changed it first. Surfaced, not retried — the user
 *              decides whether their offline intent still holds.
 *   4xx        Validation or permission. Retrying cannot help; surface the
 *              server's own message.
 *   5xx        Server-side and probably transient. Retry.
 */
export function classifyReplayError(
  status: number | undefined,
): ReplayDisposition {
  if (status === undefined) return 'retry'
  if (status === 404 || status === 410) return 'drop'
  if (status >= 400 && status < 500) return 'fail'
  return 'retry'
}

export class OfflineWriteExpiredError extends Error {
  constructor(label: string) {
    super(`"${label}" was queued too long ago to send safely.`)
    this.name = 'OfflineWriteExpiredError'
  }
}

export class OfflineWriteNeedsReviewError extends Error {
  constructor(label: string) {
    super(`"${label}" may already have been saved. Check before resending.`)
    this.name = 'OfflineWriteNeedsReviewError'
  }
}

export class OfflineWriteDroppedError extends Error {
  constructor(label: string) {
    super(`"${label}" could not be applied — the item no longer exists.`)
    this.name = 'OfflineWriteDroppedError'
  }
}

export interface OfflineRunnerContext {
  ledger: OfflineLedger
  gate: <T>(fn: () => Promise<T>) => Promise<T>
  /** Pulls an HTTP status off whatever error shape the host client throws. */
  getStatus: (error: unknown) => number | undefined
  now?: () => number
  getOwnerId: () => string | null
  captureSession?: () => { assertCurrent(): void }
  flush?: () => Promise<void>
}

/**
 * Wraps a plain API call into the `mutationFn` registered via
 * `setMutationDefaults`. Everything a rehydrated mutation needs to know about
 * itself travels in its envelope, so a mutation restored from disk into a
 * fresh process is fully self-describing.
 */
export function createOfflineMutationFn<TPayload, TResult>(
  kind: OfflineMutationKind,
  run: (envelope: OfflineMutationEnvelope<TPayload>) => Promise<TResult>,
  context: OfflineRunnerContext,
): (envelope: OfflineMutationEnvelope<TPayload>) => Promise<TResult> {
  const { ledger, gate, getStatus } = context
  const now = context.now ?? (() => Date.now())

  return envelope =>
    gate(async () => {
      assertOfflineWriteOwner(envelope, context.getOwnerId())
      const session = context.captureSession?.()
      const { idempotencyKey, label } = envelope
      const existing = ledger.get(idempotencyKey)

      // Already done. Replaying would duplicate it, so hand back what the
      // server said the first time and let callers see a normal success.
      if (existing?.status === 'succeeded') {
        return existing.result as TResult
      }

      if (now() - envelope.enqueuedAt > OFFLINE_QUEUE_MAX_AGE_MS) {
        writeEntry(ledger, envelope, existing, {
          status: 'expired',
          updatedAt: now(),
          message: 'Queued too long ago to send safely.',
        })
        throw new OfflineWriteExpiredError(label)
      }

      // A write the user has already been asked about stays parked until they
      // explicitly retry it, which clears the ledger row first.
      if (existing?.status === 'needs-review') {
        throw new OfflineWriteNeedsReviewError(label)
      }

      // `inflight` means an earlier attempt reached the network and we never
      // learned the outcome. Re-sending is only safe if a duplicate is
      // harmless; otherwise this is exactly the case worth asking about.
      if (
        existing?.status === 'inflight' &&
        REPLAY_SAFETY[kind] === 'at-most-once'
      ) {
        writeEntry(ledger, envelope, existing, {
          status: 'needs-review',
          updatedAt: now(),
          message:
            'This may already have been saved before the connection dropped.',
        })
        throw new OfflineWriteNeedsReviewError(label)
      }

      const attempts = (existing?.attempts ?? 0) + 1
      writeEntry(ledger, envelope, existing, {
        status: 'inflight',
        attempts,
        updatedAt: now(),
      })

      let dispatched = false
      try {
        // Persist the dispatch marker before I/O, so a terminated process can
        // distinguish a never-sent command from one with an unknown outcome.
        await context.flush?.()
        session?.assertCurrent()
        assertOfflineWriteOwner(envelope, context.getOwnerId())
        dispatched = true
        const result = await run(envelope)
        writeEntry(ledger, envelope, existing, {
          status: 'succeeded',
          attempts,
          updatedAt: now(),
          result,
        })
        return result
      } catch (error) {
        if (!dispatched) {
          ledger.patch(idempotencyKey, {
            status: 'queued',
            attempts: existing?.attempts ?? 0,
          })
          throw error
        }
        const status = getStatus(error)
        const disposition = classifyReplayError(status)

        if (disposition === 'drop') {
          writeEntry(ledger, envelope, existing, {
            status: 'failed',
            attempts,
            updatedAt: now(),
            message: 'The item no longer exists.',
          })
          throw new OfflineWriteDroppedError(label)
        }

        if (disposition === 'fail' || attempts >= OFFLINE_QUEUE_MAX_ATTEMPTS) {
          writeEntry(ledger, envelope, existing, {
            status: 'failed',
            attempts,
            updatedAt: now(),
            message: toMessage(error),
          })
          throw error
        }

        // Retryable. A 5xx is an answer from the server, and this API rolls the
        // transaction back on error, so even an `at-most-once` write is safe to
        // re-queue. A *silent* failure is the dangerous one: we cannot tell a
        // request that never left the device from one whose reply was lost, so
        // the row stays `inflight` and the next attempt asks the user.
        const answeredByServer = status !== undefined
        if (answeredByServer || REPLAY_SAFETY[kind] !== 'at-most-once') {
          ledger.patch(idempotencyKey, { status: 'queued', attempts })
        }
        throw error
      }
    })
}

function writeEntry(
  ledger: OfflineLedger,
  envelope: OfflineMutationEnvelope<unknown>,
  existing: OfflineLedgerEntry | undefined,
  changes: Partial<Omit<OfflineLedgerEntry, 'envelope'>>,
): void {
  ledger.upsert({
    envelope,
    status: existing?.status ?? 'queued',
    attempts: existing?.attempts ?? 0,
    updatedAt: existing?.updatedAt ?? envelope.enqueuedAt,
    result: existing?.result,
    message: existing?.message,
    ...changes,
  })
}

function toMessage(error: unknown): string {
  if (error instanceof Error && error.message) return error.message
  return 'The change could not be saved.'
}

/** True for the mutation keys this module owns. */
export function isOfflineMutationKey(key: unknown): key is readonly string[] {
  return (
    Array.isArray(key) &&
    Object.values(OFFLINE_MUTATION_KEYS).some(
      allowed =>
        allowed.length === key.length &&
        allowed.every((part, index) => part === key[index]),
    )
  )
}

/**
 * The `shouldDehydrateMutation` predicate for both persisters.
 *
 * Two conditions, both required. `isPaused` keeps settled and in-progress
 * mutations out of storage. The key check is the allowlist, and it is the
 * reason a payment or an invoice send can never reach the queue no matter what
 * else changes about how those are written.
 */
export function shouldDehydrateOfflineMutation(mutation: {
  state: { isPaused: boolean }
  options: { mutationKey?: readonly unknown[] }
}): boolean {
  return (
    mutation.state.isPaused === true &&
    isOfflineMutationKey(mutation.options.mutationKey)
  )
}

/** Rows worth showing the user: queued work plus anything awaiting a decision. */
export function selectPendingWrites(
  entries: OfflineLedgerEntry[],
): OfflineLedgerEntry[] {
  return entries
    .filter(entry => entry.status !== 'succeeded')
    .sort((a, b) => a.envelope.enqueuedAt - b.envelope.enqueuedAt)
}

/** Rows the user has to resolve by hand — nothing retries these on its own. */
export function selectBlockedWrites(
  entries: OfflineLedgerEntry[],
): OfflineLedgerEntry[] {
  return selectPendingWrites(entries).filter(
    entry =>
      entry.status === 'failed' ||
      entry.status === 'needs-review' ||
      entry.status === 'expired',
  )
}

/** Missing/older envelopes are quarantined, never assigned to the next login. */
export function isOwnedOfflineEnvelope(
  value: unknown,
): value is OfflineMutationEnvelope {
  if (!value || typeof value !== 'object') return false
  const entry = value as Partial<OfflineMutationEnvelope>
  return (
    entry.version === 2 &&
    typeof entry.ownerId === 'string' &&
    !!entry.ownerId &&
    typeof entry.sessionGeneration === 'number' &&
    Number.isSafeInteger(entry.sessionGeneration) &&
    entry.sessionGeneration >= 0 &&
    typeof entry.organizationId === 'string' &&
    !!entry.organizationId &&
    typeof entry.idempotencyKey === 'string' &&
    !!entry.idempotencyKey &&
    typeof entry.enqueuedAt === 'number' &&
    Number.isFinite(entry.enqueuedAt) &&
    typeof entry.kind === 'string' &&
    Object.prototype.hasOwnProperty.call(OFFLINE_MUTATION_KEYS, entry.kind)
  )
}

export class OfflineWriteOwnerError extends Error {
  readonly status = 409
  constructor() {
    super(
      'This change belongs to another session. Sign in to its original account to send it.',
    )
    this.name = 'OfflineWriteOwnerError'
  }
}

export function assertOfflineWriteOwner(
  value: unknown,
  ownerId: string | null,
): asserts value is OfflineMutationEnvelope {
  if (!isOwnedOfflineEnvelope(value) || !ownerId || value.ownerId !== ownerId) {
    throw new OfflineWriteOwnerError()
  }
}

export function selectOwnerWrites(
  entries: OfflineLedgerEntry[],
  ownerId: string | null,
): OfflineLedgerEntry[] {
  return ownerId
    ? entries.filter(
        entry =>
          isOwnedOfflineEnvelope(entry.envelope) &&
          entry.envelope.ownerId === ownerId,
      )
    : []
}
